import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useGetModels } from '@utils/api/models/use-get-models.ts';
import { useRealtimeContext } from '@utils/providers/realtime-context.tsx';
import { useEntitlements } from '@utils/providers/entitlements-context.tsx';
import { TokenExtension } from '@utils/extensions/token.extension.ts';
import {
  SessionSource,
  rememberSessionSource,
  streamUrlIsValid
} from '@utils/editors/live-session-source.ts';
import { RealtimeAudioSource } from '@utils/types/editors/realtime.ts';
import { RealtimeLanguageSelect } from './realtime-language-select.tsx';

type Props = {
  sessionId: string;
  source: SessionSource | null;
  onSource: (source: SessionSource) => void;
  onNew: () => void;
  freshDisabled?: boolean;
};
export const RealtimeSourceForm = ({
  sessionId,
  source,
  onSource,
  onNew,
  freshDisabled = false
}: Props) => {
  const { t } = useTranslation();
  const realtime = useRealtimeContext();
  const { data: models } = useGetModels();
  const { realtimeLanguages } = useEntitlements();
  const [audioSource, setAudioSource] = useState<RealtimeAudioSource>(
    source?.audioSource || 'stream'
  );
  const [url, setUrl] = useState(source?.streamUrl || '');
  const [language, setLanguage] = useState(source?.language || '');
  const [invalidUrl, setInvalidUrl] = useState(false);
  const [invalidLanguage, setInvalidLanguage] = useState(false);
  const [editingPreview, setEditingPreview] = useState(!source);
  useEffect(() => {
    if (!source) return;
    setAudioSource(source.audioSource);
    setUrl(source.streamUrl || '');
    setLanguage(source.language || '');
    setInvalidUrl(false);
    setInvalidLanguage(false);
  }, [source]);
  const starting = ['connecting', 'loading'].includes(realtime.connectionState);
  const microphoneActive =
    !sessionId && realtime.connectionState === 'connected';
  const locked =
    starting || microphoneActive || !!(sessionId && source && !editingPreview);
  const languages = (models?.result || []).filter(
    item => !realtimeLanguages?.length || realtimeLanguages.includes(item.key)
  );
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const hasInvalidUrl = audioSource === 'stream' && !streamUrlIsValid(url);
    const hasInvalidLanguage =
      !sessionId && !languages.some(item => item.key === language);
    if (hasInvalidUrl || hasInvalidLanguage) {
      setInvalidUrl(hasInvalidUrl);
      setInvalidLanguage(hasInvalidLanguage);
      return;
    }
    setInvalidUrl(false);
    setInvalidLanguage(false);
    const selected: SessionSource = {
      audioSource,
      streamUrl: audioSource === 'stream' ? url.trim() : undefined,
      language,
      modelType: 'fast'
    };
    onSource(selected);
    if (sessionId) {
      rememberSessionSource(sessionId, selected);
      setEditingPreview(false);
      return;
    }
    await realtime.connect({
      ...selected,
      token: TokenExtension.get().string || ''
    });
  };
  return (
    <form
      className="realtime-source-form"
      onSubmit={event => {
        void submit(event);
      }}
    >
      <div className="realtime-eyebrow">{t('live-source-heading')}</div>
      <fieldset disabled={locked}>
        <legend className="sr-only">{t('audio-source')}</legend>
        <div className="realtime-source-options">
          {(['stream', 'microphone'] as const).map(value => (
            <label
              key={value}
              className={audioSource === value ? 'is-selected' : ''}
            >
              <input
                type="radio"
                name="realtime-source"
                value={value}
                checked={audioSource === value}
                disabled={!!sessionId}
                onChange={() => setAudioSource(value)}
              />
              {t(value === 'stream' ? 'live-source-stream' : 'microphone')}
            </label>
          ))}
        </div>
        {audioSource === 'stream' && (
          <label className="realtime-field">
            <span>{t('stream-url')}</span>
            <input
              aria-label={t('stream-url')}
              aria-describedby="realtime-source-feedback"
              aria-required
              type="text"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              value={url}
              aria-invalid={invalidUrl}
              onChange={event => {
                setUrl(event.target.value);
                setInvalidUrl(false);
                realtime.clearStartError();
              }}
              placeholder="https://…"
            />
          </label>
        )}
        {(!sessionId || source?.language) && (
          <div className="realtime-field">
            <span>{t('language')}</span>
            <RealtimeLanguageSelect
              value={language}
              languages={languages}
              disabled={locked || !!sessionId}
              required={!sessionId}
              onChange={key => {
                setLanguage(key);
                setInvalidLanguage(false);
              }}
            />
          </div>
        )}
      </fieldset>
      <div
        id="realtime-source-feedback"
        className="realtime-source-feedback"
        role={
          invalidUrl || invalidLanguage || realtime.error ? 'alert' : undefined
        }
      >
        {invalidUrl
          ? t('live-source-url-invalid')
          : invalidLanguage
            ? t('live-select-language')
            : realtime.error ||
              (sessionId && !source
                ? t('live-preview-restore')
                : sessionId && editingPreview
                  ? t('live-preview-edit-help')
                  : t('live-source-help'))}
      </div>
      {!sessionId ? (
        <div className="realtime-source-actions">
          <Button
            label={
              starting
                ? t('live-checking-source')
                : microphoneActive
                  ? t('disconnect')
                  : t('connect')
            }
            type={microphoneActive ? 'button' : 'submit'}
            onClick={microphoneActive ? realtime.disconnect : undefined}
            disabled={starting}
          />
          <Button
            label={t('live-new-session')}
            type="button"
            variant="outline"
            onClick={onNew}
            disabled={freshDisabled || starting}
          />
        </div>
      ) : (
        <>
          <div className="realtime-source-actions">
            {!source || editingPreview ? (
              <Button label={t('live-show-preview')} type="submit" />
            ) : (
              <Button
                label={t('live-change-preview')}
                type="button"
                variant="outline"
                onClick={() => setEditingPreview(true)}
              />
            )}
            <Button
              label={t('live-new-session')}
              type="button"
              variant="outline"
              onClick={onNew}
              disabled={freshDisabled}
            />
          </div>
          {source && editingPreview && (
            <Button
              label={t('cancel')}
              type="button"
              variant="outline"
              onClick={() => {
                setUrl(source.streamUrl || '');
                setEditingPreview(false);
                setInvalidUrl(false);
                setInvalidLanguage(false);
              }}
              className="w-full"
            />
          )}
        </>
      )}
    </form>
  );
};
